import { atom } from "jotai";

import { atomWithStorage } from "jotai/utils";

import { atomFamily } from "jotai-family";

import type { IStudio } from "./interfaces/Studio.interface";

import {
  DEFAULT_SCENE_TRANSITION,
  type SceneTransition,
} from "../../../../entities/LivePreview/Strategy/SceneTransition";

import type { IStudioArea } from "./interfaces/sub-interfaces/Areas.interface";

import { TemplateType } from "./interfaces/sub-interfaces/Template.interface";

import type { IStudioTheme } from "./interfaces/sub-interfaces/Theme.interface";

import type {
  IStudioWidget,
  EStudioWidgetType,
} from "./interfaces/sub-interfaces/Widgets.interface";

import type { IStudioElement } from "./interfaces/sub-interfaces/Elements.interface";

import { normalizeStudioAlertSlot } from "./utils/alertSlot.utils";

export const atomModalDataActive = atom<IStudio | null>(null);

// Transición de escena por defecto (global). Override por-escena vive en model.transition.

// Es una preferencia de usuario (no un feature gate): persiste entre sesiones.

export const sceneTransitionAtom = atomWithStorage<SceneTransition>(
  "eriol.sceneTransition",

  DEFAULT_SCENE_TRANSITION,
);

export const atomModalConfigActive = atom<boolean>(false);

export const atomTemplateReady = atom(false);

export const atomSceneSelected = atom<number>(0);

export const atomCurrentTemplate = atom<TemplateType | null>(null);

export const atomShowSelectTemplate = atom<boolean>(false);

export const atomAreaSelected = atom<IStudioArea | null>(null);

export const atomShowGallery = atom(false);

export const atomShowDesigner = atom(false);

export const atomShowWidgets = atom(false);

// Pedido one-shot del canvas: al setearse junto con atomShowWidgets, el modal
// de widgets abre directo en la config de este tipo (y lo limpia al consumirlo,
// así las aperturas desde la toolbar siguen aterrizando en la grilla).
export const atomWidgetConfigTarget = atom<EStudioWidgetType | null>(null);

export const atomShowElements = atom(false);

export const atomShowFilterModal = atom(false);

export const atomEditingTextElementId = atom<string | null>(null);

export const atomShowGrid = atom(true);

export const atomSnapEnabled = atom(true);

export const atomClampAssemblerBounds = atom(true);

export const atomFilterModalAreaId = atom<string | null>(null);

export const atomShowAssembler = atom<boolean>(true);

export const atomLoaderShow = atom<boolean>(false);

export const defaultStudio: IStudio = {
  id: "default-0",

  type: TemplateType.Custom,

  version: "0.1.0",

  theme: {},

  output: {
    quality: {
      video: 1,

      audio: 1,
    },

    sizes: {
      width: 1920,

      height: 1080,
    },
  },

  areas: [],
};

export const atomStudioTheme = atom<IStudioTheme>();

export const atomStudioData = atom<IStudio | null>(null);

export const atomActiveResourceId = atom<string | null>(null);

export const atomActiveVideo = atomFamily(() => atom(false));

export const atomVideoActive = atomFamily((_id: string) => atom(false));

export const atomImageActive = atomFamily((_id: string) => atom(false));

export const atomInitialized = atom<boolean>(false);

export const atomStudio = atom(
  (get) => {
    const data = get(atomStudioData);

    const sourceAlerts = data?.alerts;

    if (!sourceAlerts?.length) return data as IStudio;

    // Self-heal alerts persisted with legacy px slot bounds. Read-only: the
    // corrected bounds reach disk on the user's next write, so there is no
    // write feedback loop here. Identity is preserved when nothing changes.
    const alerts = sourceAlerts.map(normalizeStudioAlertSlot);

    const changed = alerts.some(
      (alert, index) => alert !== sourceAlerts[index],
    );

    return (changed ? { ...data, alerts } : data) as IStudio;
  },

  (_get, set, newStudio: IStudio | null) => {
    set(atomStudioData, newStudio);
  },
);

export const atomStudioReady = atom<boolean>(false);

export const atomInitializers = atom<Record<string, boolean>>({});

export const atomInitialize = atomFamily((id: string) =>
  atom(
    (get) => {
      const map = get(atomInitializers);

      return Boolean(map[id]);
    },

    (_, set, next: boolean = true) => {
      set(atomInitializers, (prev) => ({ ...prev, [id]: next }));
    },
  ),
);

export const atomSwitchScene = atom<boolean>(false);

export const atomSwitchSource = atom<boolean>(false);

export const atomSceneImage = atomFamily(() => atom<string | null>(null));

export const atomStudioAreas = atom<IStudioArea[]>([]);

export const atomStudioWidgets = atom<IStudioWidget[]>([]);

export const atomStudioElements = atom<IStudioElement[]>([]);

// Store config state from each widget form (active state + options)

export interface IWidgetConfig {
  active: boolean;

  options: Record<string, unknown>;
}

export const atomWidgetConfigs = atom<Record<string, IWidgetConfig>>({});

export const atomElementsConfigs = atom<Record<string, IWidgetConfig>>({});

export const atomBoxState = atomFamily(() =>
  atom({
    x: 0,

    y: 0,

    width: 0,

    height: 0,
  }),
);
